/**
 * Lógica de tema (pura) — claro / escuro / sistema.
 *
 * Separada do React para ser testável sem DOM. A preferência do usuário é
 * persistida no localStorage; o modo "sistema" resolve via `prefers-color-scheme`.
 */

/** Preferência escolhida pelo usuário. */
export type ThemePref = 'light' | 'dark' | 'system';

/** Tema efetivamente aplicado (o "sistema" colapsa para um destes). */
export type AppliedTheme = 'light' | 'dark';

/** Chave de persistência no localStorage. */
export const THEME_STORAGE_KEY = 'econd.theme';

/** Preferências válidas (ordem de exibição no seletor). */
export const THEME_PREFS: readonly ThemePref[] = ['light', 'dark', 'system'] as const;

/** True se `value` é uma preferência de tema conhecida. */
export function isThemePref(value: unknown): value is ThemePref {
  return value === 'light' || value === 'dark' || value === 'system';
}

/**
 * Resolve o tema aplicado a partir da preferência e do estado do sistema.
 * `system` segue `systemPrefersDark`; `light`/`dark` são explícitos.
 */
export function resolveTheme(pref: ThemePref, systemPrefersDark: boolean): AppliedTheme {
  if (pref === 'system') return systemPrefersDark ? 'dark' : 'light';
  return pref;
}

/** Interface mínima de storage (subset do localStorage) — facilita testes. */
export interface ThemeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Lê a preferência salva; default `system` quando ausente/ inválida. */
export function readStoredPref(storage: ThemeStorage | null | undefined): ThemePref {
  try {
    const raw = storage?.getItem(THEME_STORAGE_KEY);
    return isThemePref(raw) ? raw : 'system';
  } catch {
    return 'system';
  }
}

/** Persiste a preferência (best-effort; storage indisponível não quebra a UI). */
export function storePref(storage: ThemeStorage | null | undefined, pref: ThemePref): void {
  try {
    storage?.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    // ignore: modo privado / storage cheio não deve derrubar o app
  }
}

/** Rótulo amigável (pt-BR) de cada preferência, para o seletor. */
export function themeLabel(pref: ThemePref): string {
  switch (pref) {
    case 'light':
      return 'Claro';
    case 'dark':
      return 'Escuro';
    case 'system':
      return 'Sistema';
  }
}
